/**
 * Etapa 1 — Identificação: qual é a instalação, a tensão e onde ela fica.
 */
import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useCompanyList } from '../../../../../stores/companyStore';
import { CompanySelectField } from '../../../../../components/forms';
import {
  ELECTRICAL_INSTALLATION_STATUSES,
  ELECTRICAL_INSTALLATION_TYPES,
  ROTULO_DA_CLASSE_DE_TENSAO,
  ROTULO_DO_STATUS_DA_INSTALACAO,
  ROTULO_DO_TIPO_DE_INSTALACAO,
  type ElectricalInstallationStatus,
  type ElectricalInstallationType,
} from '../../../../../types/electricalInstallation';
import type { Department } from '../../../../../types/department';
import type { IdentificationErrors, IdentificationValues } from '../ElectricalInstallationWizard.types';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

interface IdentificationStepProps {
  values: IdentificationValues;
  errors: IdentificationErrors;
  departments: Department[];
  /** Pergunta a empresa dona: só na criação, quando o cabeçalho não tem empresa ativa. */
  askCompany?: boolean;
  /** Instalação já salva: a empresa aparece, mas não muda mais. */
  companyLocked?: boolean;
  /** Modo leitura (sem `.update`) ou gravação em andamento: nenhum campo aceita digitação. */
  disabled?: boolean;
  onChange: (patch: Partial<IdentificationValues>) => void;
}

export const IdentificationStep: React.FC<IdentificationStepProps> = ({
  values,
  errors,
  departments,
  askCompany = false,
  companyLocked = false,
  disabled = false,
  onChange,
}) => {
  const companyList = useCompanyList();
  const companyListFailed = askCompany && !companyLocked && companyList.carregada && Boolean(companyList.error);
  const companyListLoading = askCompany && !companyLocked && !companyList.carregada;
  const lockedCompanyName =
    companyList.companies.find((company) => String(company.id) === values.companyId)?.tradeName ||
    companyList.companies.find((company) => String(company.id) === values.companyId)?.legalName ||
    `Empresa ${values.companyId}`;
  const showLockedCompany = companyLocked && values.companyId !== '';
  const companyFieldVisible =
    askCompany &&
    !companyLocked &&
    companyList.carregada &&
    !companyList.error &&
    companyList.companies.length !== 1;

  // Escolhida a empresa, só os setores dela servem; sem escolha (empresa ativa), vale a lista toda.
  const companyDepartments = values.companyId
    ? departments.filter((department) => String(department.companyId) === values.companyId)
    : departments;

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Diga qual é a instalação. {companyFieldVisible ? 'O nome e a empresa são obrigatórios' : 'Só o nome é obrigatório'};
        o resto você pode completar depois.
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Qual é a instalação
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {companyListLoading && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} aria-label="Carregando as empresas" />
            <Typography variant="body1">Carregando as empresas...</Typography>
          </Grid>
        )}
        {companyListFailed && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={companyList.loadCompanies} sx={{ minHeight: 44 }}>
                  Tentar de novo
                </Button>
              }
            >
              Não foi possível carregar as empresas. Sem elas não dá para escolher a dona da instalação.
            </Alert>
          </Grid>
        )}
        {showLockedCompany && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Empresa proprietária"
              value={lockedCompanyName}
              disabled
              helperText="Não muda depois de criada"
              sx={fieldSx}
            />
          </Grid>
        )}
        {askCompany && !companyLocked && !companyListFailed && (
          <CompanySelectField
            grid={{ xs: 12, md: 6 }}
            required
            label="Empresa proprietária"
            value={values.companyId}
            onChange={(companyId) => onChange({ companyId, departmentId: '' })}
            error={Boolean(errors.companyId)}
            helperText={errors.companyId || 'A instalação entra no prontuário desta empresa.'}
            disabled={disabled}
          />
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            fullWidth
            label="Setor"
            value={values.departmentId}
            onChange={(event) => onChange({ departmentId: event.target.value })}
            disabled={disabled}
            helperText="Opcional. Sem setor, a instalação entra só no laudo da empresa inteira."
            sx={fieldSx}
          >
            <MenuItem value="">Sem setor</MenuItem>
            {companyDepartments.map((department) => (
              <MenuItem key={department.id} value={String(department.id)}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            required
            label="Nome da instalação"
            value={values.name}
            onChange={(event) => onChange({ name: event.target.value })}
            error={Boolean(errors.name)}
            helperText={errors.name || 'Ex.: Quadro geral de baixa tensão'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Identificação interna"
            value={values.tag}
            onChange={(event) => onChange({ tag: event.target.value })}
            error={Boolean(errors.tag)}
            helperText={errors.tag || 'Plaqueta ou patrimônio da empresa'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            select
            fullWidth
            required
            label="Tipo de instalação"
            value={values.installationType}
            onChange={(event) => onChange({ installationType: event.target.value as ElectricalInstallationType })}
            error={Boolean(errors.installationType)}
            helperText={errors.installationType}
            disabled={disabled}
            sx={fieldSx}
          >
            {ELECTRICAL_INSTALLATION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {ROTULO_DO_TIPO_DE_INSTALACAO[type]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {values.installationType === 'OUTRA' && (
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              required
              label="Descreva o tipo"
              value={values.typeDescription}
              onChange={(event) => onChange({ typeDescription: event.target.value })}
              error={Boolean(errors.typeDescription)}
              helperText={errors.typeDescription || 'Ex.: Banco de capacitores'}
              disabled={disabled}
              sx={fieldSx}
            />
          </Grid>
        )}
      </Grid>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Tensão e localização
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Tensão nominal (volts)"
            value={values.nominalVoltage}
            onChange={(event) => onChange({ nominalVoltage: event.target.value })}
            error={Boolean(errors.nominalVoltage)}
            helperText={errors.nominalVoltage || 'Deixe em branco em para-raios e aterramento.'}
            disabled={disabled}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="body1" component="p" sx={{ mb: 1, color: 'text.secondary' }}>
            Classe de tensão
          </Typography>
          <Box sx={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>
            {values.voltageClass ? (
              <Chip
                variant="outlined"
                label={ROTULO_DA_CLASSE_DE_TENSAO[values.voltageClass]}
                sx={{ fontSize: '1rem' }}
              />
            ) : (
              <Typography variant="body1">Calculada ao salvar</Typography>
            )}
          </Box>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            O sistema define a classe a partir da tensão informada.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            select
            fullWidth
            label="Estado de uso"
            value={values.status}
            onChange={(event) => onChange({ status: event.target.value as ElectricalInstallationStatus })}
            disabled={disabled}
            helperText="Instalação desativada não gera pendência no laudo."
            sx={fieldSx}
          >
            {ELECTRICAL_INSTALLATION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {ROTULO_DO_STATUS_DA_INSTALACAO[status]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Onde fica"
            value={values.location}
            onChange={(event) => onChange({ location: event.target.value })}
            error={Boolean(errors.location)}
            helperText={errors.location || 'Ex.: Sala elétrica, galpão 1'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Observações"
            value={values.observations}
            onChange={(event) => onChange({ observations: event.target.value })}
            helperText="Algo que o laudo deva registrar"
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default IdentificationStep;
